import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchAllPaginated, syncResource, mergeById, SyncItem } from '../utils/brewfatherSync';
import { CompressedCache } from '../utils/compressedCache';
import { hasAppVersionChanged, updateCachedAppVersion } from '../utils/versionMarker';
import { APP_VERSION } from '../index';
import logger from '../utils/logger';

// Shared state machine behind BatchContext and RecipeContext: cache-first load,
// rate-limit-friendly incremental refresh, and full refetch on app updates.

interface UseBrewfatherResourceOptions {
  apiKey: string | null;
  endpoint: string;
  params?: Record<string, unknown>;
  versionDomain: string;
}

export interface BrewfatherResource<T extends SyncItem> {
  items: T[];
  isLoading: boolean;
  isRefetching: boolean;
  error: string | null;
  lastUpdated: Date | null;
  lastSynced: Date | null;
  refresh: () => Promise<void>;
  refreshInBackground: () => Promise<void>;
  ensureLoaded: () => Promise<void>;
  clearAllData: () => void;
}

export const useBrewfatherResource = <T extends SyncItem>(
  { apiKey, endpoint, params, versionDomain }: UseBrewfatherResourceOptions,
  cache: CompressedCache<T>
): BrewfatherResource<T> => {
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);

  // Mirror of `items` so refresh callbacks never merge into a stale closure
  const itemsRef = useRef<T[]>([]);
  itemsRef.current = items;

  // Deduplicate concurrent syncs (mount staleness check + manual refresh, etc.)
  const inFlightRef = useRef<Promise<void> | null>(null);

  const applyFull = useCallback((fullItems: T[]) => {
    setItems(fullItems);
    cache.set(fullItems);
    updateCachedAppVersion(versionDomain, APP_VERSION);
    setLastUpdated(new Date());
    setLastSynced(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionDomain]);

  const doSync = useCallback(async (background: boolean) => {
    if (!apiKey) return;
    if (inFlightRef.current) return inFlightRef.current;

    const run = (async () => {
      try {
        // Any in-flight sync — foreground or background — shows as busy, so
        // every view of this domain spins whenever a refresh is running.
        setIsRefetching(true);
        setError(null);

        if (hasAppVersionChanged(versionDomain, APP_VERSION)) {
          logger.log(`${endpoint}: app version changed, full refetch`);
          const fullItems = await fetchAllPaginated<T>({ endpoint, apiKey, params });
          fullItems.sort((a, b) => (b._timestamp_ms || 0) - (a._timestamp_ms || 0));
          applyFull(fullItems);
          return;
        }

        const outcome = await syncResource<T>({
          endpoint,
          apiKey,
          params,
          getCached: () => {
            const cached = cache.get();
            return cached && cached.length > 0 ? cached : itemsRef.current;
          },
        });

        if (outcome.type === 'noChanges') {
          // Restamp the staleness clock: the sync verified the cache is
          // current, so shouldRefresh() must go false or every view-open
          // staleness check would re-trigger an endless refresh loop.
          cache.touch();
          setLastSynced(new Date());
          return;
        }

        if (outcome.type === 'full') {
          applyFull(outcome.items);
          return;
        }

        const base = itemsRef.current.length > 0 ? itemsRef.current : (cache.get() || []);
        const merged = mergeById(base, outcome.items);
        setItems(merged);
        cache.set(merged);
        setLastUpdated(new Date());
        setLastSynced(new Date());
      } catch (err) {
        logger.error(`Error refreshing ${endpoint}:`, err);
        // Background refreshes fail quietly — the cached data on screen is
        // still valid; only surface errors the user explicitly asked for.
        if (!background) {
          setError(err instanceof Error ? err.message : `Failed to refresh ${endpoint}`);
        }
      } finally {
        setIsRefetching(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = run;
    return run;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, endpoint, versionDomain, applyFull]);

  const refresh = useCallback(() => doSync(false), [doSync]);
  const refreshInBackground = useCallback(() => doSync(true), [doSync]);

  // Initial full load for users with no cache at all
  const initialLoad = useCallback(async () => {
    if (!apiKey) return;
    try {
      setIsLoading(true);
      setError(null);
      const fullItems = await fetchAllPaginated<T>({ endpoint, apiKey, params });
      fullItems.sort((a, b) => (b._timestamp_ms || 0) - (a._timestamp_ms || 0));
      applyFull(fullItems);
    } catch (err) {
      logger.error(`Error loading ${endpoint}:`, err);
      setError(err instanceof Error ? err.message : `Failed to load ${endpoint}`);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, endpoint, applyFull]);

  // Load from cache once on mount
  useEffect(() => {
    const cached = cache.get();
    if (cached && cached.length > 0) {
      setItems(cached);
      const refreshedAt = cache.getLastRefreshTime();
      setLastUpdated(refreshedAt || new Date());
      setLastSynced(refreshedAt || new Date());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the API key becomes available: fetch if there is no cache, refetch if
  // the app version changed, or quietly sync if the cache has gone stale.
  useEffect(() => {
    if (!apiKey) return;

    const cached = cache.get();
    if (!cached || cached.length === 0) {
      logger.log(`${endpoint}: no cache, initial fetch`);
      const timeoutId = setTimeout(() => initialLoad(), 500);
      return () => clearTimeout(timeoutId);
    }

    if (hasAppVersionChanged(versionDomain, APP_VERSION) || cache.shouldRefresh()) {
      const timeoutId = setTimeout(() => refreshInBackground(), 1000);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  const ensureLoaded = useCallback(async () => {
    if (itemsRef.current.length > 0) {
      if (hasAppVersionChanged(versionDomain, APP_VERSION) && !isRefetching) {
        refresh().catch(() => null);
      } else if (cache.shouldRefresh() && !isRefetching) {
        // Data on screen but past its staleness window (view opened after
        // sitting elsewhere) — refresh quietly; the busy flag shows in headers.
        refreshInBackground().catch(() => null);
      }
      return;
    }

    // Views can call this before the provider's mount effect has run (child
    // effects fire first), so check the cache before resorting to a full fetch.
    const cached = cache.get();
    if (cached && cached.length > 0) {
      setItems(cached);
      const refreshedAt = cache.getLastRefreshTime();
      setLastUpdated(refreshedAt || new Date());
      setLastSynced(refreshedAt || new Date());
      return;
    }

    if (isLoading) return;
    await initialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionDomain, isLoading, isRefetching, initialLoad, refresh, refreshInBackground]);

  const clearAllData = useCallback(() => {
    setItems([]);
    setLastUpdated(null);
    setLastSynced(null);
    setError(null);
  }, []);

  return {
    items,
    isLoading,
    isRefetching,
    error,
    lastUpdated,
    lastSynced,
    refresh,
    refreshInBackground,
    ensureLoaded,
    clearAllData,
  };
};
